import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import theme, { Colors } from 'modules/theme';

import { Alert as AlertComponent, utils } from 'styled-minimal';
import Icon from 'components/Icon';

const { colors, palette } = utils.getTheme(theme);
const variants = { ...colors, ...palette };

AlertComponent.displayName = 'AlertComponent';

const AlertWrapper = styled.div`
  display: flex;
  line-height: 1;
  padding: 0;
  position: relative;
  background: ${Colors.white};
  box-shadow: 0px 0px 50px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
`;

const AlertIcon = styled.div`
  align-items: flex-start;
  background-color: ${Colors.white};
  color: #fff;
  display: flex;
  padding: ${utils.spacer(3)};
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
`;

const AlertButton = styled.button`
  background-color: ${({ variant }) => variants[variant]};
  color: #ccc;
  pointer-events: all;
  position: absolute;
  right: ${utils.spacer(3)};
  top: 50%; /* Adjusted to 50% from the top */
  transform: translateY(-50%); /* Centers the button vertically */
  cursor: pointer;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  animation: ${spin} 0.8s linear infinite;
  color: ${({ color }) => color};
  display: inline-block;
  font-size: 20px;
`;

const AlertContent = styled.div`
  align-self: center;
  color: ${({ color }) => color};
  display: flex;
  flex-direction: column;
  gap: ${utils.spacer(1)};
  padding: ${utils.spacer(2)};
  padding-right: ${({ hasClose }) => (hasClose ? utils.spacer(5) : utils.spacer(2))};
`;

const AlertAction = styled.button`
  align-self: flex-start;
  background: transparent;
  border: 0;
  color: ${Colors.lightBlue};
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding: 0;
  pointer-events: all;
  text-decoration: underline;
`;

const Alert = ({
  action,
  children,
  handleClickAction,
  handleClickClose,
  id,
  icon,
  loading,
  ...rest
}) => {
  const output = {};
  let name;
  let color;

  switch (rest.variant) {
    case 'success': {
      name = icon || 'check';
      color = Colors.green;
      break;
    }
    case 'warning': {
      name = icon || 'exclamation';
      color = Colors.orange;
      break;
    }
    case 'danger': {
      name = icon || 'times';
      color = Colors.red;
      break;
    }
    case 'info': {
      name = icon || 'question';
      color = Colors.lightBlue;
      break;
    }
    case 'dark': {
      name = icon || 'bell';
      color = Colors.black;
      break;
    }
    default: {
      name = icon || 'circle';
      color = Colors.black;
    }
  }

  if (loading) {
    name = icon || 'spinner';
  }

  if (handleClickClose) {
    output.button = (
      <AlertButton data-id={id} onClick={handleClickClose} type="button" data-testid="AlertButton">
        <Icon name="times" fontSize={10} />
      </AlertButton>
    );
  }

  if (action && handleClickAction) {
    output.action = (
      <AlertAction data-id={id} data-to={action.to} onClick={handleClickAction} type="button">
        {action.label}
      </AlertAction>
    );
  }

  return (
    <AlertWrapper {...rest} data-testid="AlertWrapper">
      <AlertIcon {...rest}>
        {loading ? (
          <Spinner className={`fas fa-${name}`} color={color} />
        ) : (
          <Icon name={name} color={color} fontSize={20} />
        )}
      </AlertIcon>
      <AlertContent color={color} hasClose={!!handleClickClose}>
        {children}
        {output.action}
      </AlertContent>
      {output.button}
    </AlertWrapper>
  );
};

Alert.propTypes = {
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  }),
  children: PropTypes.node.isRequired,
  handleClickAction: PropTypes.func,
  handleClickClose: PropTypes.func,
  icon: PropTypes.string,
  id: PropTypes.string,
  loading: PropTypes.bool,
  outline: PropTypes.bool,
  variant: PropTypes.string,
};

Alert.defaultProps = {
  outline: true,
  variant: 'info',
};

export default Alert;
